import * as angular from 'angular';
import {IPromise} from 'angular';
import NvdbSubmissionList from '../../../model/model-nvdbsubmissionlist';
import Selection from '../../../model/model-selection';
import datafangstHelpers from '../../../utilities';
import contractService, {ContractFeatureType} from '../../../service/contract/contract-services';
import datacatalog from '../../../integration/datacatalog/datacatalog-services';
import server from '../../../integration/server/server-services';
import security from '../../../service/security/security-services';
import exportParamModalHtml from './export-params-modal.html';
import confirmChangedLocationalsHtml from './export-confirm-locationals-change.html';
import {isoDateString} from "../../../time";
import {
    NvdbSubmission,
    NvdbSubmissionStatus,
    ValidationTypeIndex
} from "../../../integration/representation/nvdb-submission";
import messagesService from "../../../service/messages/messages-service";
import {Destination} from "../../../integration/representation/contract";
import FeatureList from "../../../model/model-featurelist";
import {Feature, Operation} from "../../../integration/representation/feature";
import {UserSession} from "../contract-features-3/domain";
import {Association} from "../../../integration/representation/association";
import {LocalizationChange} from "../../../integration/representation/localization-change";
import {sidePositionFromDomain} from "../../../sidePositionUtilities";
import {getFeltKodeList} from "../../../laneUtilities";

/** Parameters for export modal. */
interface ExportModalParams {
    mode: string,
    header: string,
    footer: string,
    destination: string,
    operation: string,
    effectDate?: Date,
    populateProjectRefAttribute?: boolean,
    referenceId?: string,
    overwriteLatestVersion?: boolean
}

interface ContractExportControllerState {
    contract: contractService;
    messages: messagesService;
    user: UserSession;
    nvdbSubmissions: NvdbSubmissionList;
    featureTypeSelection: Selection<number>;
    isSubmittingToNvdb: boolean;
    selectedDestination: Destination;
    featureTypes: { [key: number]: DisplayFeatureType };
    featureList: FeatureList
    canDownload: boolean;
    canSubmitToNvdb: boolean;
    filterSubmitted: boolean;
    submitabilityReportOk: boolean;
    expandSubmissionTable: boolean;
    operation: Operation;
    typeFilter: { $: string };
    security: security;
    shouldShowDangerousExportButton: boolean;
    associations: Association[];
    unselectedAssociatedFeatureTypes: { [key: string]: {id: number, name: string }[]};
    locationalChanges: LocalizationChange[];

    getFeatureTypeNames: Function;
    statusIcon: Function;
    isSelectionSubmittableToNvdb: Function;
    isSelectionDownloadable: Function;

    submitSelectionToNvdb: Function;
    dangerouslySubmitSelectionToNvdb: Function;
    formatDate: Function;
    readableErrors: Function;
    updateDestination: Function;
    filterSelected: Function;
    toggleSubmittedFilter: Function;
    getSubmitConfirmText: Function;
    validChangeset: Function;
    submittedFilter: Function;
    submitDeletionsByTypeToNvdb: Function;
    toggleSubmissionTableExpansion: Function;
    buildListOfUnselectedAssociatedFeatureTypes: Function;
    toggleTypeSelection: Function;
    openLocationalChangesModal: (overwrite: boolean) => void;

    loadingContract: boolean;
}

interface DisplaySubmission extends NvdbSubmission {
    featureTypeNames?: string;
}

// Properties are added 
interface DisplayFeatureType extends ContractFeatureType {
    featureCount?: number;
    dirty?: number;
    exporting?: number;
    issueCount?: number;
    closings?: number;
    closings_submitted?: number;
    duplicates?: number;
    validationErrorCount?: number;
    missingLocationalCount?: number;
    missingSidepositionCount?: number;
    missingLaneCount?: number;
    missingParentCount?: number;
    relationNotInChangesetCount?: number;
    totalErrorCount?: number;
}

export default function ContractExportController($q: angular.IQService,
                                                 $scope: angular.IScope,
                                                 $interval: angular.IIntervalService,
                                                 $routeParams: angular.route.IRouteParamsService,
                                                 $uibModal: angular.ui.bootstrap.IModalService,
                                                 contract: contractService,
                                                 datacatalog: datacatalog,
                                                 server: server,
                                                 security: security,
                                                 messages: messagesService) {
    const vm: ContractExportControllerState = this;

    // Exposed vars
    vm.contract = contract;
    vm.messages = messages;
    vm.nvdbSubmissions = undefined;
    vm.featureTypeSelection = new Selection(updateTable);
    vm.isSubmittingToNvdb = false;
    vm.selectedDestination = 'NVDB';
    vm.featureTypes = vm.contract.featureTypes;
    vm.featureList = vm.contract.featureList;
    vm.canDownload = false;
    vm.canSubmitToNvdb = false;
    vm.filterSubmitted = false;
    vm.submitabilityReportOk = false;
    vm.expandSubmissionTable = false;
    vm.operation = "CREATE";
    vm.locationalChanges = [];
    vm.typeFilter = {
        $: ''
    };


    // Exposed functions
    vm.getFeatureTypeNames = getFeatureTypeNames;
    vm.statusIcon = statusIcon;
    vm.isSelectionSubmittableToNvdb = isSelectionSubmittableToNvdb;
    vm.isSelectionDownloadable = isSelectionDownloadable;

    vm.submitSelectionToNvdb = submitSelectionToNvdb;
    vm.dangerouslySubmitSelectionToNvdb = dangerouslySubmitSelectionToNvdb;
    vm.formatDate = formatDate;
    vm.readableErrors = readableErrors;
    vm.updateDestination = updateDestination;
    vm.filterSelected = filterSelected;
    vm.toggleSubmittedFilter = toggleSubmittedFilter;
    vm.getSubmitConfirmText = getSubmitConfirmText;
    vm.validChangeset = validChangeset;
    vm.submittedFilter = submittedFilter.bind(this);
    vm.submitDeletionsByTypeToNvdb = submitDeletionsByTypeToNvdb;
    vm.toggleSubmissionTableExpansion = toggleSubmissionTableExpansion;

    vm.security = security;
    vm.shouldShowDangerousExportButton = false;

    vm.associations = [];

    vm.buildListOfUnselectedAssociatedFeatureTypes = buildListOfUnselectedAssociatedFeatureTypes;
    vm.unselectedAssociatedFeatureTypes = {};
    vm.toggleTypeSelection = toggleTypeSelection;

    vm.openLocationalChangesModal = openConfirmLocationalChangesModal;

    function toggleTypeSelection(featureType: DisplayFeatureType) {
        if (featureType.dirty) {
            vm.featureTypeSelection.toggleSelected(featureType.id, true);
        }
    }

    function hasExistingNvdbFeatureOperation(feature: Feature): boolean {
        return ["CORRECT", "UPDATE", "CLOSE"].indexOf(feature.operation) >= 0;
    }

    function mustHaveParent(feature: Feature): boolean {
        return datacatalog.getFeatureType(feature.typeId).parentNeeded;
    }

    function hasParent(feature: Feature): boolean {
        return vm.associations.some(assoc => assoc.childFeatureId === feature.id);
    }

    function featureMissingParent(feature: Feature): boolean {
        if (hasExistingNvdbFeatureOperation(feature)) {
            return false;
        }
        return mustHaveParent(feature) && !hasParent(feature);
    }

    function buildListOfUnselectedAssociatedFeatureTypes() {
        let unselected: { [key: string]: { id: number, name: string }[] } = {};
        vm.featureTypeSelection.getSelected()
            .forEach(featureTypeId => {
                unselected['' + featureTypeId] = getRequiredSelectedTypes(featureTypeId)
            });
        vm.unselectedAssociatedFeatureTypes = unselected;
    }

    // For the given road object type, which more more road object types have to be in the change set that are not
    // already in the change set?
    function getRequiredSelectedTypes(featureTypeId: number): { id: number, name: string }[] {

        if (vm.submitabilityReportOk) {
            return [];
        }

        const requiredTypeIds = new Set<number>();

        // Find dirty road objects of this type
        const dirtyFeatures = vm.featureList.getFeaturesByType(featureTypeId).filter(feature => feature.dirty);
        const dirtyFeatureIds = dirtyFeatures.map(feature => feature.id);

        // There are two possibilities for types that must be submitted simultaneously
        // 1. Children that are not in NVDB

        // Add required typeIds of possibility #1 to requiredTypeIds
        vm.associations // All associations with a parent and/or child in the contract
            .filter(assoc => dirtyFeatureIds.includes(assoc.parentFeatureId)) // All associations with dirty parent
            .filter(assoc => vm.featureList.isFeatureIdPresent(assoc.childFeatureId)) // Associations with children in the contract
            .map(assoc => vm.featureList.getFeatureById(assoc.childFeatureId)) // Map to feature
            .filter(feature => feature.nvdbId === 0) // Filter out children that already exist in NVDB
            .forEach(feature => requiredTypeIds.add(feature.typeId));

        // 2. Add any parents in contract if parent is needed for this feature type
        if (datacatalog.getFeatureType(featureTypeId).parentNeeded) {
            vm.associations
                .filter(assoc => dirtyFeatureIds.includes(assoc.childFeatureId)) // All associations with dirty child
                .filter(assoc => vm.featureList.isFeatureIdPresent(assoc.parentFeatureId)) // Associations with parent in the contract
                .map(assoc => vm.featureList.getFeatureById(assoc.parentFeatureId)) // Map to feature
                .forEach(feature => requiredTypeIds.add(feature.typeId));
        }

        // Remove selected types
        vm.featureTypeSelection.getSelected().forEach(typeId => requiredTypeIds.delete(typeId));

        // Map to return type
        return Array.from(requiredTypeIds).map(typeId => {
            return {id: typeId, name: vm.featureTypes[typeId].name}
        });
    }

    function toggleSubmissionTableExpansion() {
        vm.expandSubmissionTable = !vm.expandSubmissionTable;
    }

    function submittedFilter(featureType: DisplayFeatureType) {
        if (this.filterSubmitted) {
            return featureType.dirty > 0;
        } else {
            return true;
        }
    }

    function getFeatureTypeNames(nvdbSubmission: DisplaySubmission) {
        if (!nvdbSubmission.featureTypeNames) {
            const featureTypeNames = nvdbSubmission.featureTypeIds
                .map(featureTypeId => datacatalog.getFeatureTypeName(featureTypeId))
                .join(', ');
            nvdbSubmission.featureTypeNames = featureTypeNames;
        }

        return nvdbSubmission.featureTypeNames;
    }

    function statusIcon(status: NvdbSubmissionStatus) {
        if (status === 'BEHANDLES') {
            return 'spinner-arrows-bg-white';
        } else if (status === 'AVVIST') {
            return 'glyphicon glyphicon-remove-circle icon-color-error';
        } else if (status === 'VENTER') {
            return 'spinner-arrows-bg-white';
        } else if (status === 'UTFØRT') {
            return 'glyphicon glyphicon-ok-circle icon-color-ok';
        } else if (status === "KANSELLERT") {
            return 'fas fa-undo subtext'; // Needs additional class to adjust font size to match Glyphicon size
        } else {
            return 'glyphicon glyphicon-exclamation-sign';
        }
    }

    function getNumFeaturesNotSubmittedToNvdb(featureTypeId: number): number {
        const featureType = vm.featureTypes[featureTypeId];
        return featureType.dirty;
    }

    function isAllExporting(featureTypeId: number): boolean {
        const featureType = vm.featureTypes[featureTypeId];
        // In theory, exporting and dirty could be different subset, but this is the best we can do for the moment
        // We currently set "exporting" for all features in a type when we save to NVDB, this may change in the future
        return featureType.exporting >= featureType.dirty;
    }

    function isSelectionSubmittableToNvdb() {
        if (vm.isSubmittingToNvdb) {
            return false;
        } else {
            if (!vm.submitabilityReportOk) {
                return false;
            }
            const selected = vm.featureTypeSelection.getSelected();
            let numSubmittableFeatureTypes = 0;
            selected.forEach(function (featureTypeId) {
                if (getNumFeaturesNotSubmittedToNvdb(featureTypeId) > 0 && !isAllExporting(featureTypeId)) {
                    numSubmittableFeatureTypes++;
                }
            });

            return numSubmittableFeatureTypes > 0;
        }
    }

    function isSelectionDownloadable() {
        return vm.featureTypeSelection.getSelected().length > 0;
    }

    function openExportParamsModal(exportParams: ExportModalParams) {
        function controller($scope: any, $uibModalInstance: angular.ui.bootstrap.IModalInstanceService) {
            $scope.ok = function () {
                $uibModalInstance.close({type: 'clear'});
            };
            $scope.cancel = function () {
                $uibModalInstance.dismiss('cancel');
            };
            $scope.exportParams = exportParams;
        }

        controller.$inject = ['$scope', '$uibModalInstance'];

        const modalInstance = $uibModal.open({
            animation: true,
            template: exportParamModalHtml,
            controller
        });

        return modalInstance.result;
    }

    function submitDeletionsByTypeToNvdb(featureTypeId: number) {
        console.log(vm);

        let exportParams = {
            mode: "CLOSE_IN_NVDB",
            header: "Lukk i NVDB",
            footer: "Lukk",
            destination: "NVDB",
            operation: "CREATE"
        };

        openExportParamsModal(exportParams).then(function () {
            vm.isSubmittingToNvdb = true;
            server.submitClosingsToNvdb(vm.contract.contractId, [featureTypeId])
                .then((response) => {

                    // fetch did not fail
                    if (response.success) {
                        vm.contract.readFeatureList().then(() => {
                            vm.nvdbSubmissions.accept(response.data);
                            vm.isSubmittingToNvdb = false;
                        });
                    } else {
                        // User errors
                        vm.messages.danger(response.message);
                        vm.isSubmittingToNvdb = false;
                    }

                }, (rejection) => {
                    vm.isSubmittingToNvdb = false;
                    return rejection;
                });
        }, function () {
            // cancel button clicked
        });

    }

    function openConfirmLocationalChangesModal(overwrite: boolean) {
        function controller($scope: any, $uidModalInstance: angular.ui.bootstrap.IModalInstanceService) {
            $scope.cancel = () => {
                $uidModalInstance.dismiss('cancel');
            };

            $scope.getDirection = (direction: 'ALONG' | 'TOWARDS') => {
                if(direction === 'ALONG'){
                    return 'MED';
                } else if(direction === 'TOWARDS') {
                    return 'MOT'
                }
                return 'UKJENT RETNING';
            };
            $scope.getSidePosition = sidePositionFromDomain;
            $scope.getLane = (lane: string) => {
                return getFeltKodeList()
                    .find(felt => felt.value === lane)?.name ?? "UKJENT FELT";
            };

            $scope.approve = () => {
                $uidModalInstance.close();
                doSubmitSelectionToNvdb(overwrite, true);
            };

            $scope.deny = () => {
                $uidModalInstance.close();
                doSubmitSelectionToNvdb(overwrite, false);
            };

            $scope.changes = vm.locationalChanges;
            $scope.requiresImport = vm.locationalChanges.filter(change => change.requiresImport)
                .map(change => change.receivingIdentifier);
        }

        controller.$inject = ['$scope', '$uibModalInstance'];

        const modalInstance = $uibModal.open({
            animation: true,
            template: confirmChangedLocationalsHtml,
            controller
        });

        return modalInstance.result;
    }

    function dangerouslySubmitSelectionToNvdb() {
        return doSubmitSelectionToNvdb(true);
    }


    function submitSelectionToNvdb() {
        return doSubmitSelectionToNvdb(false);
    }

    function doSubmitSelectionToNvdb(overwrite: boolean, approveChanges: boolean | null = null): void {
        vm.locationalChanges = [];
        let selected = vm.featureTypeSelection.getSelected();

        // Omit feature types where all features are registered in NVDB
        selected = selected
            .filter(function (featureTypeId) {
                return getNumFeaturesNotSubmittedToNvdb(featureTypeId);
            })
            .map(function (featureTypeId) {
                return featureTypeId;
            });

        let exportParams: ExportModalParams = {
            mode: "SUBMIT_TO_NVDB",
            header: overwrite ? "Overskriv NVDB" : "Registrer i NVDB",
            footer: overwrite ? "Overskriv" : "Registrer",
            destination: "NVDB",
            effectDate: new Date(),
            populateProjectRefAttribute: vm.contract.referenceId && vm.contract.referenceId.length > 0,
            referenceId: vm.contract.referenceId || '',
            operation: "CREATE",
            overwriteLatestVersion: overwrite
        };

        openExportParamsModal(exportParams).then(function () {
            const projectRef = exportParams.populateProjectRefAttribute ? exportParams.referenceId : undefined;
            vm.isSubmittingToNvdb = true;

            let submit : IPromise<any>;
            if (overwrite) {
                submit = server.dangerouslySubmitRegistrationToNvdb(vm.contract.contractId, selected, isoDateString(exportParams.effectDate), approveChanges, projectRef);
            } else {
                submit = server.submitRegistrationToNvdb(vm.contract.contractId, selected, isoDateString(exportParams.effectDate), approveChanges, projectRef);
            }
            submit
                .then((response) => {
                    // We need to accept or deny the changes
                    if (!response.success && response.data.length > 0 && 'identifier' in response.data[0]) {
                        vm.locationalChanges = response.data;
                        vm.isSubmittingToNvdb = false;
                        openConfirmLocationalChangesModal(overwrite);
                        return;
                    }
                    server.getSubmittabilityReport($routeParams.contractId, Object.keys(vm.featureTypes).map(n => parseInt(n)))
                        .then(summary => updateSummary(summary.validationCount));
                    vm.isSubmittingToNvdb = false;
                    vm.featureTypeSelection.clear();

                    // fetch did not fail
                    if (response.success) {
                        vm.contract.readFeatureList().then(() => {
                            vm.nvdbSubmissions.accept(response.data);
                        });
                    } else {
                        // User errors
                        vm.messages.danger(response.message);
                    }

                }, (rejection) => {
                    vm.isSubmittingToNvdb = false;
                    return rejection;
                });
        }, function () {
            // cancel button clicked
        });
    }

    function updateFeatureTypeNvdbStatus() {
        server.getSubmittabilityReport($routeParams.contractId, Object.keys(vm.featureTypes).map(n => parseInt(n)))
            .then(summary => {
                updateSummary(summary.validationCount);
                updateTable();
            });
    }

    function formatDate(isoDate: string) {
        return datafangstHelpers.formatDate(new Date(isoDate));
    }

    function updateDestination() {
        vm.featureTypeSelection.clear();
        vm.featureTypes = undefined;
        vm.featureList = undefined;

        $scope.$applyAsync();

        fetchFeatures()
            .then(() => {
                $scope.$applyAsync();
            })
            .catch((error) => {
                console.error('Error fetching features on destination change:', error);
                $scope.$applyAsync();
            });
    }

    function updateTable() {
        if (vm.featureTypeSelection.getSelected().length > 0) {
            server.getSubmittabilityReport($routeParams.contractId, vm.featureTypeSelection.getSelected()).then(report => {
                updateSummary(report.validationCount);
                vm.submitabilityReportOk = report.submittable;
                vm.buildListOfUnselectedAssociatedFeatureTypes();
            });
        }
    }

    function updateSummary(validationSummary: ValidationTypeIndex) {
        Object.keys(vm.contract.featureTypes).filter(featureTypeId => validationSummary.hasOwnProperty(featureTypeId)).map((typeIdStr) => {
            const typeId = parseInt(typeIdStr);
            vm.featureTypes[typeId].featureCount = validationSummary[typeId].FEATURES;
            vm.featureTypes[typeId].dirty = validationSummary[typeId].DIRTY;
            vm.featureTypes[typeId].exporting = validationSummary[typeId].EXPORTING;
            vm.featureTypes[typeId].issueCount = validationSummary[typeId].ISSUES;
            vm.featureTypes[typeId].closings = validationSummary[typeId].CLOSINGS;
            vm.featureTypes[typeId].closings_submitted = validationSummary[typeId].CLOSINGS_SUBMITTED;
            vm.featureTypes[typeId].duplicates = validationSummary[typeId].DUPLICATES;
            vm.featureTypes[typeId].validationErrorCount = validationSummary[typeId].VALIDATION_ERRORS;
            vm.featureTypes[typeId].missingLocationalCount = validationSummary[typeId].MISSING_LOCATIONAL;
            vm.featureTypes[typeId].missingSidepositionCount = validationSummary[typeId].MISSING_SIDEPOSITION;
            vm.featureTypes[typeId].missingLaneCount = validationSummary[typeId].MISSING_LANE;
            vm.featureTypes[typeId].missingParentCount = validationSummary[typeId].MISSING_PARENT;
            vm.featureTypes[typeId].relationNotInChangesetCount = validationSummary[typeId].MISSING_RELATION_IN_CHANGESET;
            vm.featureTypes[typeId].totalErrorCount = validationSummary[typeId].ERRONOUS_FEATURES;
        });
    }

    function getSubmitConfirmText() {
        let text = "Bekreft at du ønsker å sende valgte objekttyper til NVDB. ";
        let selected = vm.featureTypeSelection.getSelected();
        const hasRemainingIssues = selected.some(typeId => {
            return vm.featureTypes[typeId].issueCount > 0;
        });
        if (hasRemainingIssues) {
            text = text + "NB! En eller flere objekttyper har utestående anmerkninger";
        }
        return text;
    }

    function filterSelected(featureType: DisplayFeatureType): boolean {
        let res = vm.featureTypeSelection.getSelected().filter(selected => selected == featureType.id)[0];
        return !!res;
    }

    function toggleSubmittedFilter() {
        vm.filterSubmitted = !vm.filterSubmitted;
    }
    function readableErrors(nvdbSubmission: NvdbSubmission) : string[] {
        let errors = nvdbSubmission.errors;
        if(!errors){ // If no errors exist in submission, return empty.
            return [];
        }
        let submissionFeatureTypes = nvdbSubmission.featureTypeIds;

        // If submission doesn't contain feature types or if the feature list is not defined,
        // readable feature aliases will not be found. Return the errors as is
        if(!Array.isArray(submissionFeatureTypes) || !vm.featureList){
            return errors;
        }
        const uuidReplace = function(uuid:string):string {
            // Assign uuid to replacement string as a fallback if a better replacement cannot be created
            let replacement = uuid;

            // Investigate the feature types of this submission
            submissionFeatureTypes.forEach(f => {
                let fullFeatureTypes = vm.featureList.features[f];
                if(!Array.isArray(fullFeatureTypes)) {
                    return;
                }
                let feature = fullFeatureTypes.find(f => f.id == uuid);
                if(!feature) {
                    return;
                }
                // If we reach here, feature object was found! Assign alias to replacement text
                replacement = feature.name;
            });
            // Return replacement string
            return replacement;
        };
        let readableErrorsInSubmission = errors.map(error => {
            // Regex for uuids with an optional "tempid" prefix. Case insensitive
            const uuidRegex = /(tempid(\s)?)?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}?/i;
            return error.replace(uuidRegex, uuidReplace);
        });
        return readableErrorsInSubmission;
    }


    function validChangeset() {
        let selected = vm.featureTypeSelection.getSelected();
        let typesWithErrors = selected.filter(typeId => {
            return vm.featureTypes[typeId].totalErrorCount > 0
                || vm.featureTypes[typeId].relationNotInChangesetCount > 0
                || vm.featureTypes[typeId].dirty === 0;
        });
        return typesWithErrors.length == 0;
    }

    function init() {
        vm.loadingContract = true;

        $q.all([security.ensureCurrentUser(), contract.selectContract($routeParams.contractId)])
            .then(async (data) => {
                vm.user = data[0];
                vm.contract = data[1];
                vm.nvdbSubmissions = new NvdbSubmissionList($interval, $routeParams.contractId, server, updateFeatureTypeNvdbStatus);
                vm.canDownload = vm.user.hasPermission('FEATURE:DOWNLOAD', $routeParams.contractId);
                vm.canSubmitToNvdb = vm.user.hasPermission('REGISTRATION_SUBMISSION:CREATE', $routeParams.contractId);
                vm.shouldShowDangerousExportButton = vm.user.hasPermission('DANGEROUSLY_OVERWRITE_NVDB:VIEW') ||
                    vm.user.isGroupAdmin(vm.contract.contractGroup);
                vm.loadingContract = false;

                return fetchFeatures();
            })
            .then(() => {
                if (Object.keys(vm.featureTypes).length === 0) {
                    console.warn("Feature types are empty. Skipping submittability report.");
                    return;
                }

                return server.getSubmittabilityReport($routeParams.contractId, Object.keys(vm.featureTypes).map(n => parseInt(n)));
            })
            .then(summary => {
                if (summary) {
                    updateSummary(summary.validationCount);
                }
            })
            .then(() => {
                return server.readAssociationsList($routeParams.contractId);
            })
            .then(result => {
                vm.associations = result;
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
                vm.loadingContract = false;
            });

        $scope.$on('$destroy', function () {
            if (vm.nvdbSubmissions) {
                vm.nvdbSubmissions.destroy();
            }
        });
    }

    function fetchFeatures() {
        return $q(function (resolve, reject) {
            let featureFetchPromise;

            if (vm.selectedDestination === 'NVDB') {
                featureFetchPromise = vm.contract.readFeatureList();
            } else if (vm.selectedDestination === 'FKB') {
                featureFetchPromise = vm.contract.readFKBFeatureList();
            } else {
                console.warn("No valid destination selected, skipping feature fetching.");
                resolve();
                return;
            }

            featureFetchPromise
                .then(() => {
                    vm.featureTypes = vm.selectedDestination === 'NVDB' ? vm.contract.featureTypes ?? {} : vm.contract.fkbFeatureTypes ?? {};
                    vm.featureList = vm.selectedDestination === 'NVDB' ? vm.contract.featureList : vm.contract.fkbFeatureList;
                    return runFeatureDependentFunctions();
                })
                .then(() => {
                    resolve();
                })
                .catch(error => {
                    console.error('Error fetching features:', error);
                    reject(error);
                });
        });
    }

    function runFeatureDependentFunctions() {
        if (Object.keys(vm.featureTypes).length === 0) {
            console.warn("Feature types are empty. Skipping submittability report.");
            return $q.resolve();
        }

        return $q.all([
            server.getSubmittabilityReport($routeParams.contractId, Object.keys(vm.featureTypes).map(n => parseInt(n)))
                .then(summary => {
                    if (summary) {
                        updateSummary(summary.validationCount);
                    }
                }),

            server.readAssociationsList($routeParams.contractId)
                .then(result => {
                    vm.associations = result;
                })
        ]).then(() => {});
    }

    init();
}

ContractExportController.$inject = ['$q', '$scope', '$interval', '$routeParams', '$uibModal', 'contract', 'datacatalog', 'server', 'security', 'messages'];
